import { Redirect } from 'react-router-dom'
import { useSelector } from 'react-redux'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import * as Sentry from '@sentry/browser'

import ASRIntroduction from './Components/ASRIntroduction'
import ASRTeacherModal from './Components/ASRTeacherModal'
import ASRTeacherModeView from './Components/ASRTeacherModeView'
import ASRInitialSpeechReminders from './Components/ASRInitialSpeechReminders'
import ASRAfterInitialSpeechTimer from './Components/ASRAfterInitialSpeechTimer'
import StudentMicProgressView from './Components/StudentMicProgressView'
import { ASR_EVENTS, ASR_STATES, ASR_STATES_PROGRESS, MINIMUM_ACCURACY_TO_PASS } from './StudentASRConstants'

import useStudentPreChecks from '../useStudentPreChecks'
import Loading from '../../Global/Loading';
import { PRE_CHECK_MACHINE_STATES } from '../constants'

import { amiraGestures } from '../../Coach/GesturedSpeech'
import { COPY_STRINGS } from '../../../constants/translatedCopy'
import { selectReaderManager } from '../../Reader/ReaderManagerEvents/ReaderManagerSlice'
import useWindowEvents from '../useWindowEvents'
import { endUniversalSocketSession } from '../../../services/SpeechStream'
import { getCopyByGradeband, GRADE_BANDS } from '../../../services/ThemingService'
import { sanitizeDataForSentry } from '../../../services/util'

export default function StudentASRChecks(props) {
    /**
     * props
     */
    const {readerManagerRef, history, speechProps, toggleReaderContainer, readerContainerVisible, midSessionMicTroubleshoot, audioIssueType, midSessionTroubleshootSeen, onRestartSession, onRestartSessionSuppressingTroubleshoot, hideMicIcon } = props

    /**
     * states
     */
    const [currentASRState, setASRState] = useState(ASR_STATES.INITIAL)
    // Initialize to true for mid-session troubleshoot to prevent the redirect to /asr
    // before the mount effect can set it. Without this, the first render fires the
    // redirect guard (!isUrlCurrent && !showTeacherModal) and launches the ASR story reader.
    const [showTeacherModal, setShowTeacherModal] = useState(midSessionMicTroubleshoot)
    const [teacherMode, setTeacherMode] = useState(false)

    const [onScreenText, setOnScreenText] = useState("");
    const [showOnScreenText, setShowOnScreenText] = useState(false);

    const hasLoggedVolumeStats = useRef(false)
    const teacherASRAttempts = useRef(0); // Track the number of attempts for teacher ASR tests
    /**
     * Check if this is a teacher test from URL parameter
     */
    const isTeacherTest = new URLSearchParams(window.location.search).get('teacherTest') === 'true'

    useEffect(() => {
        const studentChecks = getCopyByGradeband(COPY_STRINGS, 'student_checks', { gradeOverrides: { 2: GRADE_BANDS.MEDIUM } });
        const copy = studentChecks.student_asr_on_screen_text
        setOnScreenText(copy)
    }, [])
    
    /**
     * Set teacher mode if this is a teacher test
     */
    useEffect(() => {
        if (isTeacherTest) {
            setTeacherMode(true)
            // Reset showTeacherModal when we're on the ASR route for teacher test
            setShowTeacherModal(false)
        }
    }, [isTeacherTest])

    /**
     * Mutable values
     */
    const studentDidTry = useRef(false)
    const teacherTestSuccessful = useRef(false)
    const teacherTestTimeoutRef = useRef(null)

    /**
     * redux states
     */
    const readerManagerStates = useSelector(selectReaderManager)
    const isListening = useSelector(state => state.speech?.listener?.isListening ?? false)

    /**
     * hooks
     */
    useWindowEvents()
    const studentPreCheck = useStudentPreChecks({ itemToCheck: PRE_CHECK_MACHINE_STATES.studentASRChecks })
    const { isCurrent } = studentPreCheck

    const isUrlCurrent = document.location.pathname.includes('/student/pre-checks/asr')
    const hasHighBackgroundNoise = readerManagerStates.contextStates.bgNoiseTriggered // determines signal is low, noise is high
    const waitingForStudentSpeech = !studentDidTry.current
    const waitingForPhraseEnd = !readerManagerStates.contextStates.phraseEnded
    const waitingForStoryEnd = !readerManagerStates.contextStates.isCurrentStoryEnded

    /**
     * initialize this check
     */
    useEffect(() => {
        if (isCurrent) {
            if (midSessionMicTroubleshoot) {
                // Skip ASR check, go directly to teacher modal
                // Make sure the reader container background is visible
                if(!readerContainerVisible) toggleReaderContainer()
                setShowTeacherModal(true);
            } else {
                startASRCheck();
            }
        }

        // Cleanup: stop recording when component unmounts (always register this)
        return () => {
            if (isCurrent) {
                speechProps.stopListening();
            }
        }
    }, [])

    /**
     * wait to kick off ASR check stages when reader manager starts the ASR story
     */
    useEffect(()=>{
        // run this for retry flow
        if(readerManagerStates.isCurrentStoryInitialPhraseStarted && currentASRState === ASR_STATES.INITIAL){
            setASRState(ASR_STATES.INTRODUCTION_SCREEN_1)
        }
        if(readerManagerStates.contextStates.currentWord){
            studentDidTry.current = true
        }
    },[readerManagerStates])

    /**
     * wait to dispatch finish check when reader manager ends the story
     */
    useEffect(()=>{
        if(readerManagerStates.isCurrentStoryEnded){
            studentPreCheck.logEvent('STORY_END_CONDITIONS_MET', undefined, {
                isTeacherTest: isTeacherTest,
                currentAccuracy: readerManagerStates.contextStates.currentAccuracy
            })
            
            if(isTeacherTest){
                // For teacher tests, check for high background noise first
                const accuracy = readerManagerStates.contextStates.currentAccuracy
                const success = accuracy >= MINIMUM_ACCURACY_TO_PASS
                const hasHighBackgroundNoise = readerManagerStates.contextStates.bgNoiseTriggered
                                
                // Log the result
                if(success){
                    studentPreCheck.logEvent(ASR_EVENTS.TEACHER_TEST_SUCCESS)
                } else {
                    studentPreCheck.logEvent(ASR_EVENTS.TEACHER_TEST_FAILED)
                }
                
                // Check for high background noise - this takes precedence over success/failure
                if(hasHighBackgroundNoise){
                    studentPreCheck.logEvent(ASR_EVENTS.TEACHER_TEST_BG_NOISE_DETECTED)
                    // Navigate back with background noise result
                    history.push('/student/pre-checks?teacherTestResult=backgroundNoise')
                } else {
                    // Navigate back with success/failure result
                    if(success){
                        // Teacher test was successful - speak additional details prompt before redirecting
                        const studentChecks = getCopyByGradeband(COPY_STRINGS, 'student_checks');
                        speechProps.speakWithPromise({ textToSpeak: studentChecks.teacher_mode_asr_success })
                            .then(() => {
                                history.push('/student/pre-checks?teacherTestResult=success')
                            })
                            .catch(error => {
                                console.error('Error speaking additional details prompt:', error)
                                history.push('/student/pre-checks?teacherTestResult=success')
                            })
                    } else {
                        history.push('/student/pre-checks?teacherTestResult=failed')
                    }
                }
            } else {
                finishASRCheck()
            }
        }
    }, [readerManagerStates.isCurrentStoryEnded])

    /**
     * Handle showing teacher modal when returning from a teacher test with a result.
     * Runs once on mount — the teacherTestResult URL param is set via history.push
     * before this component mounts, so it's already present. Using [showTeacherModal]
     * as a dependency caused a loop: modal close → effect re-run → URL still has
     * param → modal re-opens immediately.
     */
    useEffect(() => {
        const teacherTestResult = new URLSearchParams(window.location.search).get('teacherTestResult')
        if (teacherTestResult && !showTeacherModal) {
            setShowTeacherModal(true)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * Log volume stats when story starts (moved to useEffect to avoid setState during render)
     */
    useEffect(() => {
        if (currentASRState === ASR_STATES.INTRODUCTION_SCREEN_1 && 
            readerManagerStates.isCurrentStoryInitialPhraseStarted && 
            !hasLoggedVolumeStats.current) {
            hasLoggedVolumeStats.current = true;
            const volumeCheckData = studentPreCheck.getCheckData(PRE_CHECK_MACHINE_STATES.studentASRChecks)
            if(volumeCheckData){
                const { event, ...restData } = volumeCheckData;
                if (event) {
                    studentPreCheck.logEvent(event, undefined, restData); // restData will hold the timeStamp and the event data (min / max volume)
                }
            }
        }
    }, [currentASRState, readerManagerStates.isCurrentStoryInitialPhraseStarted])

    /**
     * Methods
     */
    const startASRCheck = () => {
        if(!readerContainerVisible) toggleReaderContainer()
        studentPreCheck.startCheck()
        goToNextState()
    }
    const retry = () => {
        endUniversalSocketSession();
        // this renders the ReaderManager component that runs the story
        history.push("/student/pre-checks/asr")
        studentPreCheck.retryWith(resetState)
    }
    
    const finishASRCheck = async () => {
        const playWrapUpSpeech = async () => {
            speechProps.playDance({
                gesture: amiraGestures.ThumbsUp
            })
            
            const studentChecks = getCopyByGradeband(COPY_STRINGS, 'student_checks');
            try{
                studentPreCheck.logEvent('SPEAK_WITH_PROMISE_STARTED', undefined, {
                    textToSpeak: studentChecks.finish_asr_check
                })
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.finish_asr_check })
                studentPreCheck.logEvent('SPEAK_WITH_PROMISE_RESOLVED')
                speechProps.abortDance()
            }catch(e){
                studentPreCheck.logEvent('SPEAK_WITH_PROMISE_ERROR', undefined, {
                    error: e?.message || String(e),
                    errorStack: e?.stack
                })
                console.error(e)
            }
            
            studentPreCheck.finishCheck()
            // Close the socket connection so that the next story will
            // start with a fresh one with updated settings.
            endUniversalSocketSession()
        }
        
        // Stop the recording before playing wrap-up speech
        speechProps.stopListening();
        studentPreCheck.logEvent('CALLING_ABORT_SPEECH')
        speechProps.abortSpeech(playWrapUpSpeech)
    }

    const openTeacherModal = (accuracy = null) => {
        hideMicIcon();

        // Log to Sentry when student fails ASR check
        Sentry.captureMessage('ASR_CHECK_FAILED', {
            level: 'warning',
            tags: {
                component: 'StudentASRChecks',
            },
            extra: sanitizeDataForSentry({
                accuracy: accuracy,
                minimumAccuracy: MINIMUM_ACCURACY_TO_PASS,
                studentDidTry: studentDidTry.current,
                hasHighBackgroundNoise: hasHighBackgroundNoise
            })
        });
        
        // Stop the recording before showing the teacher modal
        speechProps.stopListening();
        setShowTeacherModal(true)
        // this unmounts the ReaderManager component that runs the story.
        history.push("/student/pre-checks")
    }

    const resetState = () => {
        setShowTeacherModal(false)
        setTeacherMode(false)
        teacherTestSuccessful.current = false
        studentDidTry.current = false
        setASRState(ASR_STATES.INITIAL)
        setShowOnScreenText(false)
    }
    
    const goToNextState = () => {
        const currentASRStateIndex = ASR_STATES_PROGRESS.indexOf(currentASRState)
        if (currentASRStateIndex < ASR_STATES_PROGRESS.length - 1) {
            setASRState(ASR_STATES_PROGRESS[currentASRStateIndex + 1])
            // Show the on-screen text when moving from introduction to activity started
            if (currentASRState === ASR_STATES.INTRODUCTION_SCREEN_1) {
                setShowOnScreenText(true)
            }
        }
    }

    const incrementTeacherASRAttempts = useCallback(() => {
        teacherASRAttempts.current++;
    }, []);

    const resetTeacherASRAttempts = useCallback(() => {
        teacherASRAttempts.current = 0;
    }, []);

    /**
     * Start rendering
     */
    if (!studentPreCheck.isCurrent) {
        return null
    }

    // Check if we're returning from a teacher test with a result
    const teacherTestResult = new URLSearchParams(window.location.search).get('teacherTestResult')
    
    if (!isUrlCurrent && !showTeacherModal && !teacherTestResult) {
        return <Redirect to="/student/pre-checks/asr" />
    }

    if (showTeacherModal || teacherTestResult){
        return <ASRTeacherModal
            speechProps={speechProps}
            handleRetry={retry}
            studentDidTry={studentDidTry}
            hasHighBackgroundNoise={hasHighBackgroundNoise}
            history={history}
            teacherASRAttempts={teacherASRAttempts.current}
            incrementTeacherASRAttempts={incrementTeacherASRAttempts}
            resetTeacherASRAttempts={resetTeacherASRAttempts}
            midSessionMicTroubleshoot={midSessionMicTroubleshoot}
            audioIssueType={audioIssueType}
            midSessionTroubleshootSeen={midSessionTroubleshootSeen}
            onRestartSession={onRestartSession}
            onRestartSessionSuppressingTroubleshoot={onRestartSessionSuppressingTroubleshoot}
        />
    }

    if (currentASRState === ASR_STATES.INTRODUCTION_SCREEN_1 && readerManagerStates.isCurrentStoryInitialPhraseStarted){
        return <div>
          <ASRIntroduction speechProps={speechProps} goToNextState={goToNextState} teacherMode={teacherMode}/>
        </div>
    }

    // The spinner covers both dead windows of this check, and it has to live here rather
    // than in ReaderManager: the reader container is display:none for the whole pre-checks
    // ASR check (Reader.scss .asrCheckReadingActivity), so ReaderManager's own loading
    // overlay renders inside a hidden subtree and is never seen.
    //   - Before the first phrase arms: story fetch, activity creation, the per-story LM
    //     graph prime and the local-ASR capability warmup all happen here (TT-1217), and
    //     Amira is silent too because ASRIntroduction is gated on the phrase having
    //     started. Without this the student watches an empty screen.
    //   - After the phrase ends, while the story finishes.
    //
    // TODO(TT-1173): `waitingForStoryEnd` is permanently true. It reads
    // contextStates.isCurrentStoryEnded, but that flag lives at the ROOT of the slice
    // (ReaderManagerSlice.js:25) and is dispatched there via notifyStoryEnded — nothing
    // ever writes it into contextStates. So the second clause below is really just
    // `!waitingForPhraseEnd`. Left as-is rather than repointed at the root flag, because
    // that would change when the spinner disappears at story end on a flow that is about
    // to be verified on device; the `||` should not be read as coordinating more than it
    // does.
    const isShowingLoading = !readerManagerStates.isCurrentStoryInitialPhraseStarted
        || (!waitingForPhraseEnd && waitingForStoryEnd)
    const shouldShowText = false; // showOnScreenText && !isShowingLoading // Disabling this for now

    // Visibility condition for the mic progress UI
    const showMicProgress = readerManagerStates.isCurrentStoryInitialPhraseStarted 
        && waitingForPhraseEnd 
        && !showTeacherModal;

    return <div>
        {shouldShowText && <div>{onScreenText}</div>}
        {showMicProgress && <StudentMicProgressView isListening={isListening} />}
        { waitingForStudentSpeech && <ASRInitialSpeechReminders speechProps={speechProps} teacherMode={teacherMode} openRetryModal={openTeacherModal} history={history} setShowTeacherModal={setShowTeacherModal} readerManagerRef={readerManagerRef}/> }
        {!waitingForStudentSpeech && waitingForPhraseEnd && <ASRAfterInitialSpeechTimer teacherMode={isTeacherTest} speechProps={speechProps} openTeacherModal={openTeacherModal} history={history} readerManagerRef={readerManagerRef} receivedValidTranscript={readerManagerStates.contextStates.receivedValidTranscript}/> }
        { waitingForPhraseEnd && <ASRTeacherModeView teacherMode={teacherMode}/> }
        { isShowingLoading && <Loading /> }

    </div>
}
